import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';

import { IncludeExcludeModel } from '../../Misc/models/include-exclude-model';
import { JournodexGeographyModel } from '../../Journodex/models/journodex-geography.model';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';
import { TargetingPreferencesService } from './targeting-preferences.service';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { JournodexApiService } from '../../../api/journodex/journodex.api.service';
import { CoreCacheService } from '../../../Core/Cache/services/core-cache.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';

@Injectable()
export class TargetingGeographyService extends JournodexRegionsService implements OnDestroy {

    protected subscriptions: Subscription[] = [];

    private geographyPreferences: IncludeExcludeModel<JournodexGeographyModel>;

    constructor(
        protected targetingPreferencesService: TargetingPreferencesService,
        protected journodexApiService: JournodexApiService,
        protected coreCacheService: CoreCacheService,
        protected cookieService: CookieService,
        protected currentUserStateService: CurrentUserStateService,
        protected localizationService: LocalizationService
    ) {
        super(journodexApiService, coreCacheService, cookieService, currentUserStateService, localizationService);

        this.subscriptions.push(targetingPreferencesService.accountGeoPreferences$.subscribe(prefs => {
            this.geographyPreferences = prefs;
        }));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    getContinents(): Observable<JournodexGeographyModel[]> {
        return super.getContinents().pipe(map(geos => {
            geos.forEach(geo => {
                geo.isLocked = this.shouldBeExcluded(geo) || !this.shouldBeExpandable(geo);
            });
            return geos;
        }));
    }

    getChildRegions(parentRegion: JournodexGeographyModel): Observable<JournodexGeographyModel[]> {
        return super.getChildRegions(parentRegion).pipe(map(geos => {
            geos.forEach(x => {
                x.isLocked = !(this.shouldBeIncluded(x) || this.shouldBeExpandable(x));
                x.isLocked = x.isLocked || this.shouldBeExcluded(x);
            });
            return geos;

        }));
    }

    searchGeographicLocationsWithAccountRestrictions(searchString: string) {
        searchString = searchString.replace(/[,\(\)\-]/g, ' ');
        return this.journodexApiService.performQuickSearchGeography(searchString, this.locale, true).pipe(map(
            quickSearchGeographicResponse => {
                // save locations to cache
                return quickSearchGeographicResponse.map(apiEntity => new JournodexGeographyModel(apiEntity, this.locale, this.localizationService));
            }
        ));
    }

    isInGeography(container: JournodexGeographyModel, contained: JournodexGeographyModel) {
        switch (container.geographicType) {
            case GeographyTypesEnum.continent : return contained.continent.id === container.continent.id;
            case GeographyTypesEnum.country : return contained.country && contained.country.id === container.country.id;
            case GeographyTypesEnum.province : return contained.province && contained.province.id === container.province.id;
            case GeographyTypesEnum.county : return contained.county && contained.county.id === container.county.id;
            case GeographyTypesEnum.city : return contained.city && contained.city.id === container.city.id;
        }
    }

    shouldBeIncluded(geo: JournodexGeographyModel): boolean {
        if (this.geographyPreferences.include.length === 0) {
            return true;
        }
        for (const includedGeo of this.geographyPreferences.include) {
            if (this.isInGeography(includedGeo, geo) || includedGeo.collectionId === geo.collectionId) {
                return true;
            }
        }
        return false;
    }

    shouldBeExpandable(geo: JournodexGeographyModel): boolean {

        if (this.geographyPreferences.include.length === 0) {
            return true;
        }

        for (const includedGeo of this.geographyPreferences.include) {
            if (this.isInGeography(geo, includedGeo)) {
                return true;
            }
        }

        return false;
    }

    shouldBeExcluded(geo: JournodexGeographyModel): boolean {
        if (this.geographyPreferences.exclude.length === 0) {
            return false;
        }
        for (const excludedGeo of this.geographyPreferences.exclude) {
            if (this.isInGeography(excludedGeo, geo) || excludedGeo.collectionId === geo.collectionId) {
                return true;
            }
        }
        return false;
    }
}
